import React, { useEffect, Suspense, lazy } from 'react'
import { css } from '@emotion/core'
import { AuthRoute, Redirect, Route, Switch, useLocation } from '@fs/zion-router'
import { targetEnv } from '@fs/zion-config'
import ErrorBoundary from '@fs/zion-error-boundary'
import { colors, Row } from '@fs/zion-ui'
import { NoticeLoading } from '@fs/zion-icon'
import { TreeToTreeSkeleton } from '@fs/zion-linker'
import SourceLinkerSkeletonFallback from './components/linker/SourceLinkerSkeletonFallback'

const AdminConsole = React.lazy(() => import('./components/AdminConsole/AdminConsole'))
const AttachedTo = React.lazy(() => import('./components/AttachedTo/AttachedTo'))
const ChangeLog = React.lazy(() => import('./components/ChangeLog/ChangeLog'))
const SourceBox = React.lazy(() => import('./components/SourceBox/SourceBox'))
const SourceListPage = React.lazy(() => import('./components/TestingPages/SourceListPage'))
const SourceViewEdit = React.lazy(() => import('./components/ViewEdit/SourceViewEdit'))
const SourceCreateAttach = React.lazy(() => import('./components/CreateAttach/SourceCreateAttach'))
const SourceCreateAttachTestPage = React.lazy(() => import('./components/TestingPages/SourceCreateAttachTestPage'))
const SourceLinkerPage = lazy(() => import('./components/linker/SourceLinkerPage'))
const TreeToTreePage = lazy(() => import('./components/linker/tree-to-tree/TreeToTreePage'))
const FullPageReload = lazy(() => import('./components/FullPageReload'))

const fullViewportHeightCss = css`
  min-height: 100vh;
`

const loadingCss = css`
  padding-top: 65px;
`
const linkVisitedCss = css`
  & a:visited {
    color: ${colors.blue50};
  }
`

function App({ setShowFooter, setIsSourceBox }) {
  const location = useLocation()

  // Remove `openedFolderId` in state after refreshing page.
  useEffect(() => {
    if (location.state?.openedFolderId) delete location.state.openedFolderId
    // We only want to run this on the initial render to clear this value after a refresh. We want to keep the data as the user navigates within the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setShowFooter(!location.pathname.match(/\/(changelog|sourcebox)/i))
    if (location.pathname.toLowerCase().includes('/sourcebox')) {
      setIsSourceBox(true)
    }
  }, [location.pathname, setIsSourceBox, setShowFooter])

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <Row css={loadingCss} alignX="center">
            <NoticeLoading size="lg" />
          </Row>
        }
      >
        <div css={linkVisitedCss}>
          <Switch>
            {/* Only run locally - a route in which to develop zion-source-list more easily */}
            {targetEnv === 'local' && <AuthRoute exact path="/sourceList/:entityId" component={SourceListPage} />}
            {/* Not used in Prod - Test page to facilitate UI testing of the Source Create Attach (Partner) page */}
            {(targetEnv === 'local' || targetEnv === 'int' || targetEnv === 'beta') && (
              <Route exact path="/sourceCreateAttachTest/:personId" component={SourceCreateAttachTestPage} />
            )}
            {/* Reroutes old URL formats to new URL formats */}
            <AuthRoute
              exact
              path="/sourceVE"
              component={() => {
                const searchParams = new URLSearchParams(location.search)
                const sourceId = searchParams.get('sourceId')
                searchParams.delete('sourceId')
                return <Redirect to={`/viewedit/${sourceId}?${searchParams.toString()}`} />
              }}
            />
            <AuthRoute
              exact
              path="/changelog"
              component={() => {
                const searchParams = new URLSearchParams(location.search)
                const sourceId = searchParams.get('sourceId')
                searchParams.delete('sourceId')
                return <Redirect to={`/changelog/${sourceId}?${searchParams.toString()}`} />
              }}
            />
            {/* Reroutes old URL format to new URL format for the Create and Attach (Partner) page */}
            <AuthRoute
              exact
              path="/sourceCA"
              component={() => {
                const searchParams = new URLSearchParams(location.search)
                const personId = searchParams.get('personId')?.toUpperCase()
                searchParams.delete('personId')
                return personId ? <Redirect to={`/createattach/${personId}`} /> : <Redirect to="/createattach/" />
              }}
            />
            <AuthRoute exact path="/admin" component={AdminConsole} />

            <AuthRoute LoadingComponent={SourceLinkerSkeletonFallback} path="/search/linker">
              <div css={fullViewportHeightCss}>
                <Suspense fallback={<SourceLinkerSkeletonFallback />}>
                  <SourceLinkerPage />
                </Suspense>
              </div>
            </AuthRoute>
            <AuthRoute LoadingComponent={TreeToTreeSkeleton} path="/search/tree/linker">
              <div css={fullViewportHeightCss}>
                <Suspense fallback={<TreeToTreeSkeleton />}>
                  <TreeToTreePage />
                </Suspense>
              </div>
            </AuthRoute>

            {/* @fs/zion-linker has client-side Links to other /search/... pages that search-react still owns (e.g. the
                "Search in Family Tree" button to /search/tree/name). Those Links are not marked `external`, so the
                Router's handledExternalRoutes cannot redirect them; this catch-all forces a full page load instead so
                the server routes them to the right app rather than falling through to Source Box. */}
            <Route path="/search" component={FullPageReload} />

            <AuthRoute exact path="/:page(changelog)/:sourceId" component={ComponentWithState(ChangeLog)} />
            <AuthRoute exact path="/:page(viewedit)/:sourceId" component={ComponentWithState(SourceViewEdit)} />
            <AuthRoute exact path="/:page(attachedto)/:sourceId" component={ComponentWithState(AttachedTo)} />
            <AuthRoute exact path="/:page(createattach)/:personId" component={ComponentWithState(SourceCreateAttach)} />
            <AuthRoute exact path="/:page(createattach)/" component={ComponentWithState(SourceCreateAttach)} />
            <AuthRoute exact path="/:page(sourceBox)" component={ComponentWithState(SourceBox)} />
            <Route component={() => <Redirect to="/SourceBox" />} />
          </Switch>
        </div>
      </Suspense>
    </ErrorBoundary>
  )
}
export default App

// FIXME: Putting in a less than optimal 'fix' to make it stay in context when page is refreshed.
//        Clicking 'Source Box' in the page heading will redraw with no context

function ComponentWithState(Component) {
  const location = useLocation()
  const previousState = location.state ?? {}
  if (location.pathname.includes('createattach')) {
    return Component
  }
  if (!location.search) {
    location.state = {}
    return Component
  }
  const searchParams = new URLSearchParams(location.search)
  if (searchParams.has('context')) {
    return Component
  }
  searchParams.delete('sourceId')
  const state = {}
  searchParams.forEach((value, key) => {
    state[key] = value
  })

  return () => (
    <Redirect
      to={{
        pathname: location.pathname,
        search: state.entityId || state.personId ? 'context' : '',
        state: Object.assign(previousState, state),
      }}
    />
  )
}
